import styles from './DeleteModelDialog.module.scss';
import { Trans } from 'react-i18next';
import ErrorMessage from '@cas/components/state-management/error-message/ErrorMessage';
import { useTranslation } from 'react-i18next';
import {
  VdsButton,
  VdsDialog,
  VdsDialogActionFooter,
  VdsDialogBody,
  VdsDialogHeader,
  VdsDialogModal,
} from '@volt/volt-extended-components-react';
import { ReactElement, useCallback, useRef } from 'react';
import { ApiBaseQueryError, ModelName } from '@casops/store/api';
import DeleteDialogButton, {
  DeleteDialogButtonProps,
} from './components/button/DeleteDialogButton';
import { useNavigate } from 'react-router-dom';

interface DeleteModelDialogProps extends DeleteDialogButtonProps {
  modelName: ModelName;
  itemName: string;
  deleteModelCallback: () => Promise<void>;
  error: ApiBaseQueryError;
  isLoading: boolean;
  navigateTo?: string; // Optional path to navigate to after successful deletion.
}

export default function DeleteModelDialog({
  modelName,
  itemName,
  deleteModelCallback,
  error,
  isLoading,
  disabled = false,
  isIconButton = true,
  navigateTo,
}: DeleteModelDialogProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const ref = useRef<HTMLVdsDialogElement>(null);

  const onDeleteClick = useCallback(async () => {
    try {
      await deleteModelCallback();
      ref?.current?.close();
      if (navigateTo) {
        navigate(navigateTo);
      }
    } catch (err) {
      console.error(err);
    }
  }, [deleteModelCallback, navigate, navigateTo]);

  if (!disabled) {
    return (
      <VdsDialog
        ref={ref}
        key={`delete-model--${modelName}`}
        padding="spacing-07"
        backdrop={true}
        width="650px"
        className={styles.preventMisalign}
      >
        <DeleteDialogButton isIconButton={isIconButton} disabled={false} />

        <VdsDialogModal>
          <VdsDialogHeader slot="header" show-close-button={false}>
            {t('Model.Delete.Title', { modelName })}
          </VdsDialogHeader>

          <VdsDialogBody slot="body" className={styles.body}>
            <p className={styles.content}>
              <Trans
                i18nKey={'Model.Delete.Body'}
                values={{ modelName, itemName }}
                components={{ b: <b />, strong: <strong /> }}
              />
            </p>
            {typeof error !== 'undefined' && (
              <ErrorMessage message={t('Model.Delete.Error', { modelName })} />
            )}
          </VdsDialogBody>

          <VdsDialogActionFooter slot="footer" class="vds__dialog__footer--end">
            <VdsButton
              variant="tertiary"
              close-dialog=""
              disabled={isLoading}
              style={{ width: 120 }}
              isFullWidth
            >
              {t('Cancel')}
            </VdsButton>
            <VdsButton
              variant="primary"
              danger={true}
              onClick={onDeleteClick}
              disabled={isLoading}
              style={{ width: 120 }}
              isFullWidth
            >
              {t('Delete')}
            </VdsButton>
          </VdsDialogActionFooter>
        </VdsDialogModal>
      </VdsDialog>
    );
  } else {
    return <DeleteDialogButton isIconButton={isIconButton} disabled={true} />;
  }
}
